import React from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserModal = ({
  show,
  onClose,
  onSubmit,
  data,
  onChange,
  isAdmin,
  isSpecialAgent,
  currentUser,
  isSubmitting,
}) => {
  if (!show) return null;

  const isEditMode = Boolean(data?._id);

  const ALL_SERVICES = [
    "smartrc",
    "Authorized_Rc",
    "parivaanrc",
    "parivaanrc2",
    "digilockerrc",
    "smartrclite",
    "parivaanrclite",
    "parivaanrc2lite",
    "digilockerrclite",
    "smartrc_chassis",
    "parivaanrc_chassis",
    "parivaanrc2_chassis",
    "digilockerrc_chassis",
    "aadhaar",
    "pan",
    "voterId",
    "drivingLicense",
    "DL_Signature",
    "rc_mobile",
    "challan",
    "findpan",
    "Aadhar_pan",
    "Number_pan",
    "PUCC",
  ];

  // Get current user's service access and prices
  const currentUserServices = currentUser?.serviceAccess || [];
  const currentUserPrices = currentUser?.servicePrices || {};

  // Filter services based on user role and access
  const availableServices = isAdmin
    ? ALL_SERVICES
    : isSpecialAgent
      ? currentUserServices
      : [];

  const handleServicePriceChange = (service, value) => {
    if (value === "") {
      onChange({
        target: {
          name: "servicePrices",
          value: {
            ...data.servicePrices,
            [service]: "",
          },
        },
      });
      return;
    }

    const numericValue = Number(value);

    if (numericValue < 0) return;

    onChange({
      target: {
        name: "servicePrices",
        value: {
          ...data.servicePrices,
          [service]: numericValue,
        },
      },
    });
  };

  const handleServiceAccessChange = (service, checked) => {
    let updatedAccess = data.serviceAccess || [];
    updatedAccess = checked
      ? [...updatedAccess, service]
      : updatedAccess.filter((s) => s !== service);

    onChange({
      target: {
        name: "serviceAccess",
        value: updatedAccess,
      },
    });
  };

  // Format price for display (handles empty values)
  const getDisplayPrice = (service) => {
    const price = data.servicePrices?.[service];
    return price === undefined || price === null ? "" : price;
  };

  const validateForm = () => {
    if (!data.name || !data.email || (!isEditMode && !data.password)) {
      alert("Please fill all required fields");
      return false;
    }

    if (isSpecialAgent) {
      const invalidServices = (data.serviceAccess || []).filter((service) => {
        const subAgentPrice = parseFloat(data.servicePrices?.[service]) || 0;
        const specialAgentPrice = parseFloat(currentUserPrices[service]) || 0;
        return subAgentPrice <= specialAgentPrice;
      });

      if (invalidServices.length > 0) {
        toast.error(
          `Prices for services must be equal to or higher than your prices`,
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(e);
    }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEditMode ? "Edit User" : "Create New User"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={data.name || ""}
                onChange={onChange}
                placeholder="Full Name"
                className="w-full border p-2 rounded-md"
                required
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={data.email || ""}
                onChange={onChange}
                placeholder="Email"
                className={`w-full border p-2 rounded-md ${
                  isEditMode ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
                disabled={isEditMode}
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone
              </label>
              <input
                id="phone"
                type="text"
                name="phone"
                value={data.phone || ""}
                onChange={onChange}
                placeholder="Phone Number"
                className="w-full border p-2 rounded-md"
              />
            </div>

            {!isEditMode && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={data.password || ""}
                  onChange={onChange}
                  placeholder="Password"
                  className="w-full border p-2 rounded-md"
                  required
                />
              </div>
            )}

            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                value={data.gender || ""}
                onChange={onChange}
                className="w-full border p-2 rounded-md"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="age"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Age
              </label>
              <input
                id="age"
                type="number"
                name="age"
                value={data.age || ""}
                onChange={onChange}
                placeholder="Age"
                min="18"
                max="100"
                className="w-full border p-2 rounded-md"
              />
            </div>
          </div>

          {/* Service Prices */}
          {(isAdmin || isSpecialAgent) && availableServices.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Prices
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableServices.map((service) => (
                  <div key={service} className="flex items-center">
                    <span className="w-32 capitalize">{service}</span>
                    <input
                      type="number"
                      value={getDisplayPrice(service)}
                      onChange={(e) =>
                        handleServicePriceChange(
                          service,
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      className="w-24 border p-1 rounded ml-2"
                      min={isSpecialAgent ? currentUserPrices[service] : 0}
                    />
                    {isSpecialAgent && (
                      <span className="text-xs text-gray-500 ml-1">
                        (Min: {currentUserPrices[service] || 0})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Access */}
          {(isAdmin || isSpecialAgent) && availableServices.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Access
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availableServices.map((service) => (
                  <label key={service} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={data.serviceAccess?.includes(service) || false}
                      onChange={(e) =>
                        handleServiceAccessChange(service, e.target.checked)
                      }
                      className="mr-1"
                    />
                    <span className="capitalize">{service}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Status */}
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Status
            </label>
            <select
              id="status"
              name="status"
              value={data.status || "active"}
              onChange={onChange}
              className="w-full border p-2 rounded-md"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Actions */}
          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Updating...
                </>
              ) : isEditMode ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default UserModal;
